import { useMemo, useState, useEffect } from 'react';
import { scrubPII }           from '../utils/scrubPII';
import { validateReportData } from '../utils/validateReportData';
import { runInsightEngine }   from '../utils/insightEngine';
import { fetchAIProse }       from '../utils/fetchAIProse';
import { buildTemplateProse } from '../utils/templateProse';

export function useInsightReport(rawData) {
  const [proseStatus, setProseStatus] = useState('idle');
  const [prose, setProse]             = useState(null);

  // Synchronous pipeline — instant, no API dependency
  const { validatedData, insightPacket, error } = useMemo(() => {
    try {
      const scrubbed  = scrubPII(rawData);
      const validated = validateReportData(scrubbed);
      const packet    = runInsightEngine(validated);
      return { validatedData: validated, insightPacket: packet, error: null };
    } catch (err) {
      console.error('[useInsightReport]', err.message);
      return { validatedData: null, insightPacket: null, error: err.message };
    }
  }, [rawData]);

  // Async AI prose — fills in after rule engine output is visible
  useEffect(() => {
    if (!validatedData || !insightPacket) return;

    const controller = new AbortController();
    setProseStatus('loading');

    fetchAIProse(validatedData, insightPacket, controller.signal)
      .then(result => {
        if (result) {
          setProse(result);
          setProseStatus('ready');
        } else {
          setProse(buildTemplateProse(validatedData, insightPacket));
          setProseStatus('fallback');
        }
      })
      .catch(err => {
        if (err.name === 'AbortError') return;  // Panel closed — do nothing
        console.error('[useInsightReport] fetchAIProse error:', err.message);
        setProse(buildTemplateProse(validatedData, insightPacket));
        setProseStatus('fallback');
      });

    return () => controller.abort();  // Cancel on unmount or rawData change
  }, [validatedData, insightPacket]);

  return {
    validatedData,
    insightPacket,
    prose,
    isProseLoading: proseStatus === 'loading',
    isProseReady:   proseStatus === 'ready' || proseStatus === 'fallback',
    usedFallback:   proseStatus === 'fallback',
    hasData:        !!validatedData,
    error,
  };
}
